import React, { createContext, useContext, useState, useEffect, useRef, useCallback } from 'react';
import type { LogEntry, ProgressData, OcrStatus, WsMessage } from '../types';

interface WebSocketState {
  isConnected: boolean;
  sessionId: string | null;
  activeSessions: number;
  logs: LogEntry[];
  progress: ProgressData;
  ocrStatus: OcrStatus | null;
  lastChunkComplete: WsMessage | null;
  setSessionId: (id: string | null) => void;
  clearLastChunkComplete: () => void;
  clearLogs: () => void;
}

const WebSocketContext = createContext<WebSocketState | null>(null);

export function useWebSocket(): WebSocketState {
  const ctx = useContext(WebSocketContext);
  if (!ctx) throw new Error('useWebSocket must be used within WebSocketProvider');
  return ctx;
}

export function WebSocketProvider({ children }: { children: React.ReactNode }) {
  const [isConnected, setIsConnected] = useState(false);
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [activeSessions, setActiveSessions] = useState(0);
  const [logs, setLogs] = useState<LogEntry[]>([]);
  const [progress, setProgress] = useState<ProgressData>({ current: 0, total: 0, step: '' });
  const [ocrStatus, setOcrStatus] = useState<OcrStatus | null>(null);
  const [lastChunkComplete, setLastChunkComplete] = useState<WsMessage | null>(null);

  const wsRef = useRef<WebSocket | null>(null);
  const reconnectRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const completionTimerRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const sessionIdRef = useRef(sessionId);

  useEffect(() => { sessionIdRef.current = sessionId; }, [sessionId]);

  const clearLastChunkComplete = useCallback(() => setLastChunkComplete(null), []);

  const addLog = useCallback((level: string, message: string, data?: any, timestamp?: string, sid?: string | null) => {
    const entry: LogEntry = {
      id: Date.now() + Math.random(),
      level,
      message,
      data: data ?? undefined,
      timestamp: timestamp || new Date().toISOString(),
      sessionId: sid ?? undefined,
    };
    setLogs(prev => [...prev.slice(-200), entry]);
  }, []);

  const clearLogs = useCallback(() => setLogs([]), []);

  useEffect(() => {
    const connect = () => {
      const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
      const wsHost = window.location.hostname + ':8443';
      const wsUrl = `${protocol}//${wsHost}/ws/debug`;

      try {
        const ws = new WebSocket(wsUrl);
        wsRef.current = ws;

        ws.onopen = () => {
          setIsConnected(true);
        };

        ws.onclose = () => {
          setIsConnected(false);
          reconnectRef.current = setTimeout(connect, 3000);
        };

        ws.onerror = () => setIsConnected(false);

        ws.onmessage = (event) => {
          try {
            const msg: WsMessage = JSON.parse(event.data);
            const msgSid = msg.session_id;

            if (msg.active_sessions !== undefined) {
              setActiveSessions(msg.active_sessions);
            }

            if (msg.type === 'status' && msg.status === 'started' && msgSid) {
              // Cancel any pending completion timer from a previous session
              // so it doesn't wipe this new session's state (Bug 3 fix)
              if (completionTimerRef.current) {
                clearTimeout(completionTimerRef.current);
                completionTimerRef.current = undefined;
              }
              setSessionId(msgSid);
              sessionIdRef.current = msgSid;
              setOcrStatus(null);
              setLastChunkComplete(null);
              try {
                const saved: string[] = JSON.parse(localStorage.getItem('activeSessions') || '[]');
                if (!saved.includes(msgSid)) {
                  saved.push(msgSid);
                  localStorage.setItem('activeSessions', JSON.stringify(saved));
                }
              } catch {}
            }

            const mySid = sessionIdRef.current;
            if (mySid && msgSid && msgSid !== mySid) {
              // Still log it (for "show all" mode) but don't update progress/ocr
              if (msg.type === 'log') {
                addLog(msg.level || 'info', msg.message || '', msg.data, msg.timestamp, msgSid);
              }
              return;
            }

            if (msg.type === 'status' && msg.status === 'ocr_started') {
              setOcrStatus({ status: 'started', message: msg.message || 'Оцифровка сканов...' });
            }
            if (msg.type === 'status' && msg.status === 'ocr_completed') {
              setOcrStatus({ status: 'completed', message: msg.message || 'Оцифровка завершена' });
              setTimeout(() => setOcrStatus(null), 2000);
            }

            if (msg.type === 'progress' && msg.data?.ocr_current !== undefined) {
              setOcrStatus({
                status: 'progress',
                message: msg.step || 'OCR...',
                current: msg.data.ocr_current,
                total: msg.data.ocr_total,
                percent: msg.data.percent,
              });
            } else if (msg.type === 'progress') {
              if (!sessionIdRef.current && msgSid) {
                setSessionId(msgSid);
                sessionIdRef.current = msgSid;
              }
              setProgress({ current: msg.current || 0, total: msg.total || 0, step: msg.step || '' });
            }

            if (msg.type === 'status' && msg.status === 'completed') {
              localStorage.removeItem('activeSessions');
              if (completionTimerRef.current) clearTimeout(completionTimerRef.current);
              completionTimerRef.current = setTimeout(() => {
                completionTimerRef.current = undefined;
                setProgress({ current: 0, total: 0, step: '' });
                setSessionId(null);
                sessionIdRef.current = null;
                setOcrStatus(null);
              }, 1000);
            }

            if (msg.type === 'chunk_complete') {
              setLastChunkComplete(msg);
            }

            if (msg.type === 'log') {
              addLog(msg.level || 'info', msg.message || '', msg.data, msg.timestamp, msgSid);
            } else if (msg.type === 'status') {
              addLog('info', `[${(msg.status || '').toUpperCase()}] ${msg.message || ''}`, null, msg.timestamp, msgSid);
            } else if (msg.type === 'progress') {
              addLog('info', `${msg.step}: ${msg.current}/${msg.total}${msg.details ? ` - ${msg.details}` : ''}`, null, msg.timestamp, msgSid);
            }
          } catch {}
        };
      } catch {}
    };

    connect();

    return () => {
      if (reconnectRef.current) clearTimeout(reconnectRef.current);
      if (wsRef.current) wsRef.current.close();
    };
  }, [addLog]);

  const value: WebSocketState = {
    isConnected,
    sessionId,
    activeSessions,
    logs,
    progress,
    ocrStatus,
    lastChunkComplete,
    setSessionId,
    clearLastChunkComplete,
    clearLogs,
  };

  return <WebSocketContext.Provider value={value}>{children}</WebSocketContext.Provider>;
}
